/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { NIFormData } from '../../react/components/form/services';
import { getType } from "typesafe-actions";
import { AppAction } from "..";
import { FormMap, loadFormActions, loadFormsActions, removedInaccessibleForms, fetchFormActions, fetchFormsActions, saveFormActions, setActiveDefinition, FormDescriptor } from "./actions";
import { apply } from 'json-patch';
import * as Sentry from "@sentry/react";

export type FormPatchAction = {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.AddPatch;
  meta: string;
} | {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.RemovePatch;
  meta: string;
} | {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.ReplacePatch;
  meta: string;
} | {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.MovePatch;
  meta: string;
} | {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.CopyPatch;
  meta: string;
} | {
  type: "DFS_EVAL_UPDATE";
  payload: jsonpatch.TestPatch;
  meta: string;
}

export interface FormsState {
  items: FormMap;
  activeFormDefinition?: FormDescriptor | null;
  /** 
   * Local minor versions are considered stale while definitions are being fetched.
   */
  stale: boolean;
  inFlightCount: number;
}

export const formsReducer = (state: FormsState | undefined = { items: {}, stale: false, inFlightCount: 0 }, action: AppAction) => {
  switch (action.type) {
    case getType(loadFormsActions.success):
      const groupedForms: FormMap = action.payload;
      return {
        ...state,
        items: groupedForms,
      };

    case getType(loadFormActions.success):
      const versions = state.items[action.payload.form_id] || [];
      if (!versions[action.payload.version_major]) {
        versions[action.payload.version_major] = action.payload;
        return {
          ...state,
          items: {
            ...state.items,
            [action.payload.form_id]: versions,
          },
        };
      }
      return state;

    case getType(fetchFormActions.request):
      return {
        ...state,
        inFlightCount: state.inFlightCount + 1
      };

    case getType(fetchFormsActions.request):
      return {
        ...state,
        stale: true
      };

    case getType(fetchFormsActions.failure):
      return {
        ...state,
        stale: false
      };

    case getType(saveFormActions.success):
      return {
          ...state,
          inFlightCount: state.inFlightCount - 1
        }

    case getType(saveFormActions.failure):
      return {
          ...state,
          inFlightCount: state.inFlightCount - 1
        }

    case getType(fetchFormActions.failure):
      return {
          ...state,
          inFlightCount: state.inFlightCount - 1
        }

    case getType(setActiveDefinition):
      return {
          ...state,
          activeFormDefinition: action.payload
        }

    case getType(removedInaccessibleForms):
      return {
        ...state,
        stale: false
      };
  }

  return state;
};

/**
 * Apply a single patch to an evaluation. This has the potential to result in a patch conflict, particularly in cases where one
 * evaluation is edited on multiple devices:
 *    * the patch being applied is from another device, being applied over patches that are on the current device
 *    * the patch is from the current device, being applied over patches that come from another device
 * Typically this only happens with deletions, where one patch deletes a field, then a subsequent patch tries to either:
 *    * delete that field again:
 *      1. Device A: {op: 'remove', path: '/info/evaluator2'}
 *      2. Device B: {op: 'remove', path: '/info/evaluator2'}
 *    * add a field that is a descendant of the deleted field:
 *      1. Device A: {op: 'remove', path: '/info/evaluator2'}
 *      2. Device B: {op: 'add', path: '/info/evaluator2/name', value: 'bob'}
 *
 * As a safety precaution, just ignore any patch that results in a conflict. Report to Sentry for the sake of traceability.
 */
 export function formUpdateReducer(state: NIFormData, action: FormPatchAction) {

  if (action.type !== 'DFS_EVAL_UPDATE') {
    return state
  }

  const t = action.payload;

  if (t === undefined) {
    return state;
  }

  // here - apply
  let a;

  try {
    a = apply(JSON.parse(JSON.stringify(state)), [t]);
  } catch(e) {
    console.warn('Unable to apply patch:', JSON.stringify(t));

    Sentry.withScope(scope => {
      scope.setExtras({handled: true});
      Sentry.captureException(e);
    })

    return state;
  }

  return a;
}